import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import useLocalStorage from "hooks/useLocalStorage";

/**
 * Creates a React context for authentication.
 * @returns {React.Context} A React context object for authentication.
 */
const AuthContext = createContext();

/**
 * Custom hook to access the authentication context.
 * @returns {object} The authentication context with user data and authentication functions.
 */
export const useAuth = () => useContext(AuthContext);

/**
 * AuthProvider is a context provider component that manages authentication state and provides
 * authentication-related functionalities to its children components.
 *
 * It handles user login, logout, and maintains the current user's authentication state.
 * It also manages the loading state while authentication state is being determined,
 * and any login errors that might occur.
 * @param {object} props - Props for the AuthProvider component.
 * @param {React.ReactNode} props.children - Child components that will have access to the authentication context.
 * @returns {React.ReactElement} A context provider component wrapping its children.
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useLocalStorage("currentUser", "");
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null); // State for handling login errors

  /**
   * Authenticates a user using their username and password.
   * If successful, sets the current user and stores the session token.
   * In case of failure, sets an error message.
   * @param {string} username - The username of the user trying to log in.
   * @param {string} password - The password of the user trying to log in.
   * @async
   * @returns {Promise<void>} A promise that resolves when the login process is complete.
   */
  const login = async (username, password) => {
    try {
      setLoginError(null);
      setLoginLoading(true);
      const response = await axios.post(`${window?.globalConfig.rvlApiUrl}/api/Login`, {
        username,
        password,
      });

      // Check if login is successful
      if (response.data.success) {
        const { sessionToken, user } = response.data; // Assuming the API returns a sessionToken
        localStorage.setItem("sessionToken_dealer", sessionToken);
        setCurrentUser({ username, token: sessionToken, ...user });
        setLoginError(null);
        setLoginLoading(false);
        return {
          success: true,
          sessionToken
        };
      } else {
        // Handle unsuccessful login attempts
        setLoginError(response.data.message || "Login failed"); // Set the error message
        setCurrentUser(null);
        setLoginLoading(false);
        return {
          success: false,
        };
      }
    } catch (error) {
      console.error("Login error", error);
      setLoginLoading(false);
      setLoginError("An error occurred during login");
      return {
        success: false
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("sessionToken_dealer");
    setCurrentUser(null);
  };

  useEffect(() => {
    // const token = localStorage.getItem("token");
    // if (token) {
    //   // Optionally verify token validity
    //   setCurrentUser({ token });
    // }
    setLoading(false);
  }, []);

  const value = {
    currentUser,
    login,
    logout,
    loginLoading,
    loginError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
